import React, { useContext, useState } from 'react';
import * as S from './Prompt.style';
import { authApi, finalUrl } from '../../services/apis';
import { RootStoreContext } from '../../App';

const getApiHostBaseUrl = () => {
	try {
		const parsed = new URL(finalUrl);
		return `${parsed.protocol}//${parsed.host}`;
	} catch (e) {
		return '';
	}
};

const Prompt = () => {
	const { likuStore } = useContext(RootStoreContext);

	const [editablePrompt, setEditablePrompt] = useState('');
	const [sourcePrompt, setSourcePrompt] = useState(
		'원본 프롬프트 내용이 여기에 표시됩니다.',
	);
	const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
	const [isPromptTreeOpen, setIsPromptTreeOpen] = useState(true);

	const formatPromptParts = (promptParts) => {
		if (typeof promptParts === 'string') {
			return promptParts;
		}

		if (Array.isArray(promptParts)) {
			return promptParts
				.map((item) => {
					if (typeof item === 'string') return item;
					if (item && typeof item === 'object') return item.text || '';
					return '';
				})
				.filter(Boolean)
				.join('\n\n');
		}

		if (promptParts && typeof promptParts === 'object') {
			if ('text' in promptParts) {
				return String(promptParts.text || '');
			}
			return Object.values(promptParts)
				.filter((value) => value !== null && value !== undefined)
				.map((value) => {
					if (typeof value === 'string') return value;
					if (value && typeof value === 'object' && 'text' in value) {
						return String(value.text || '');
					}
					return '';
				})
				.filter(Boolean)
				.join('\n\n');
		}

		return '';
	};

	const fetchPromptByType = async (type) => {
		setIsLoadingPrompt(true);

		try {
			const hostBaseUrl = getApiHostBaseUrl();
			const promptUrl = hostBaseUrl
				? `${hostBaseUrl}/type-prompts/${type}`
				: `/type-prompts/${type}`;

			const { data } = await authApi.get(promptUrl);
			const resolvedPrompt = formatPromptParts(data?.data?.prompt_parts);
			setSourcePrompt(resolvedPrompt || 'prompt_parts 데이터가 없습니다.');
		} catch (error) {
			console.error('Failed to fetch prompt:', error);
		} finally {
			setIsLoadingPrompt(false);
		}
	};

	const fetchPrompt = async (type) => {
		const selectedLikuSerial = likuStore?.thisLiku?.serial || '';

		if (!selectedLikuSerial) {
			alert('연결된 Liku가 없습니다. Liku를 먼저 연결해 주세요.');
			return;
		}

		setIsLoadingPrompt(true);

		try {
			const hostBaseUrl = getApiHostBaseUrl();
			const promptUrl = hostBaseUrl
				? `${hostBaseUrl}/prompts/${selectedLikuSerial}`
				: `/prompts/${selectedLikuSerial}`;

			if (type === 'get') {
				let nextEditablePrompt = '데이터가 없습니다.';
				try {
					const { data } = await authApi.get(promptUrl);
					nextEditablePrompt =
						formatPromptParts(data?.data?.prompt_text) || '데이터가 없습니다.';
				} catch (error) {
					if (error?.response?.status !== 404) {
						throw error;
					}
				}
				setEditablePrompt(nextEditablePrompt);
			} else {
				await authApi.put(promptUrl, {
					prompt_text: editablePrompt,
				});
				alert('저장되었습니다.');
			}
		} catch (error) {
			console.error('Failed to fetch prompt:', error);
		} finally {
			setIsLoadingPrompt(false);
		}
	};

	const handlePromptTypeClick = async (event) => {
		const { value } = event.currentTarget;
		await fetchPromptByType(value);
	};

	const handlePromptClick = async (event) => {
		const { value } = event.currentTarget;
		await fetchPrompt(value);
	};

	const handleCopyToEditable = () => {
		const confirmed = window.confirm(
			'복사하기 버튼 클릭시 입력한 내용이 사라집니다. 정말 복사하시겠습니까?',
		);
		if (!confirmed) return;
		setEditablePrompt(sourcePrompt);
	};

	return (
		<S.Container>
			<S.SectionsRow>
				<S.TreeSection>
					<S.TreeWrap>
						<S.TreeHeader
							type='button'
							onClick={() => setIsPromptTreeOpen((prev) => !prev)}>
							<span>{isPromptTreeOpen ? '▼' : '▶'}</span>
							기본 프롬프트
						</S.TreeHeader>
						{isPromptTreeOpen && (
							<S.TreeChildren>
								<S.SideButton
									type='button'
									value='Kid'
									onClick={handlePromptTypeClick}
									disabled={isLoadingPrompt}
									className='treeChild'>
									{isLoadingPrompt ? 'Loading...' : 'Kid'}
								</S.SideButton>
								<S.SideButton
									type='button'
									value='Senior'
									onClick={handlePromptTypeClick}
									disabled={isLoadingPrompt}
									className='treeChild'>
									{isLoadingPrompt ? 'Loading...' : 'Senior'}
								</S.SideButton>
							</S.TreeChildren>
						)}
					</S.TreeWrap>
				</S.TreeSection>

				<S.RightSection>
					<S.PromptEditorsSection>
						<S.SourcePromptSection>
							<S.Label>리쿠의 기본 프롬프트 (읽기 전용)</S.Label>
							<S.EditBox readOnly value={sourcePrompt} />
						</S.SourcePromptSection>

						<S.CopySection>
							<S.CopyButton type='button' onClick={handleCopyToEditable}>
								&#9654;
							</S.CopyButton>
						</S.CopySection>

						<S.LastSection>
							<S.Label>사용자 프롬프트 (입력하기)</S.Label>
							<S.EditBox
								value={editablePrompt}
								onChange={(e) => setEditablePrompt(e.target.value)}
								placeholder='수정할 프롬프트를 입력하세요.'
							/>
						</S.LastSection>
					</S.PromptEditorsSection>

					<S.BottomActionRow>
						<S.BottomActionItem>
							<S.BottomActionLabel>
								현재 연결된 리쿠 프롬프트 불러오기
							</S.BottomActionLabel>
							<S.SideButton
								type='button'
								value='get'
								onClick={handlePromptClick}
								disabled={isLoadingPrompt}>
								{isLoadingPrompt ? 'Loading...' : '불러오기'}
							</S.SideButton>
						</S.BottomActionItem>
						<S.BottomActionItem>
							<S.BottomActionLabel>
								내가 작성한 프롬프트 현재 연결된 리쿠에 적용하기
							</S.BottomActionLabel>
							<S.SideButton
								type='button'
								value='put'
								onClick={handlePromptClick}
								disabled={isLoadingPrompt}>
								{isLoadingPrompt ? 'Loading...' : '저장하기'}
							</S.SideButton>
						</S.BottomActionItem>
					</S.BottomActionRow>
				</S.RightSection>
			</S.SectionsRow>
		</S.Container>
	);
};

export default Prompt;
